// Minimal, dependency-free JWT payload reader.
//
// The SPA only ever READS claims for UI decisions (which nav to show, whether a
// forced password change is pending). It does NOT verify the signature — the
// backend is the sole authority on token validity (see Story 2.5). Adding a
// jwt-decode dependency would be redundant, so we decode the base64url payload
// by hand.

export interface AccessClaims {
  /** Subject — the user id. */
  sub: string;
  email: string;
  role: "super_admin" | "org_admin" | "clinician" | string;
  org_id?: string | null;
  org_role?: string | null;
  /** Set by Story 2.7 — true when the user must change their password first. */
  force_password_change?: boolean;
  /** Story 16.2 — account provenance (e.g. "webapp_legacy"); drives the legacy gate. */
  user_origin?: string | null;
  /** Story 16.2 — super_admin certificate-verification verdict (false until 16.4). */
  cert_verified?: boolean;
  /**
   * Franklin#445 — account-enabled flag (FR39). Drives the legacy gate: a
   * stage-2 legacy clinician (cert_verified=true) stays gated until this is true
   * (Story 16.7 / Stripe). Absent on pre-#445 tokens → read as not-active.
   */
  is_active?: boolean;
  /**
   * Story 8.1 (FR38) — legacy-import calibration bypass. True ⇒ the SPA
   * CalibrationGate passes synchronously (no status query). Absent on pre-8.1
   * tokens → the gate falls through to GET /calibration/status (fail-safe:
   * costs a query, never opens the backend CALIB-001 gate).
   */
  already_certified?: boolean;
  jti?: string;
  type?: string;
  iat?: number;
  exp?: number;
}

/** Decode the (UTF-8 safe) base64url payload of a JWT. Returns null if malformed. */
export function decodeJwt(token: string | null | undefined): AccessClaims | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    // Decode as UTF-8 so non-ASCII claim values survive.
    const json = decodeURIComponent(
      binary
        .split("")
        .map((char) => "%" + char.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const claims = JSON.parse(json);
    if (!claims || typeof claims !== "object") return null;
    return claims as AccessClaims;
  } catch {
    return null;
  }
}
